import React, { useState, useEffect, useCallback } from 'react';
import { X, Trophy, Medal, Crown, Film, Gamepad2, Users, Shield, Flame, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import axios from 'axios';
import { API_URL } from '../config/api';

const PERIOD_TABS = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'daily', label: 'Today' },
  { key: 'weekly', label: 'Week' },
  { key: 'monthly', label: 'Month' },
  { key: 'all_time', label: 'All-Time' },
];

const getPeriodDesc = (period) => {
  const now = new Date();
  const est = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  if (period === 'daily') {
    return est.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (period === 'yesterday') {
    const y = new Date(est);
    y.setDate(est.getDate() - 1);
    return y.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (period === 'weekly') {
    const day = est.getDay();
    const sunday = new Date(est);
    sunday.setDate(est.getDate() - day);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    return `${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${saturday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  if (period === 'monthly') {
    return est.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  return '';
};

const getRankIcon = (rank) => {
  if (rank === 1) return <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />;
  if (rank === 2) return <Medal className="h-4 w-4 sm:h-5 sm:w-5 text-gray-300" />;
  if (rank === 3) return <Medal className="h-4 w-4 sm:h-5 sm:w-5 text-amber-700" />;
  return <span className="text-xs sm:text-sm text-gray-500 font-mono w-5 text-center">{rank}</span>;
};

const LeaderboardTable = ({ data, type }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Trophy className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 opacity-30" />
        <p className="text-xs sm:text-sm">No rankings yet. Be the first!</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid={`leaderboard-${type}-list`}>
      {data.map((entry, i) => (
        <div
          key={entry.user_id || entry.club_id || i}
          className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg transition-colors ${
            i === 0 ? 'bg-amber-900/20 border border-amber-800/40' : 'bg-zinc-800'
          }`}
          data-testid={`leaderboard-entry-${i}`}
        >
          <div className="w-6 sm:w-8 flex justify-center flex-shrink-0">{getRankIcon(entry.rank)}</div>
          {type === 'clubs' ? (
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-purple-700 flex items-center justify-center text-white text-xs sm:text-sm font-semibold flex-shrink-0">
              <Users className="h-4 w-4" />
            </div>
          ) : (
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs sm:text-sm font-semibold flex-shrink-0 overflow-hidden">
              {entry.profile_picture
                ? <img src={entry.profile_picture} alt="" className="w-full h-full object-cover" />
                : entry.username?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-white font-medium truncate flex items-center gap-1">
              {type === 'clubs' ? entry.club_name : `@${entry.username}`}
              {type === 'games' && entry.winning_streak >= 2 && (
                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-bold ${
                  entry.winning_streak >= 7 ? 'bg-red-500/20 text-red-400' :
                  entry.winning_streak >= 4 ? 'bg-orange-500/20 text-orange-400' :
                  'bg-amber-500/20 text-amber-400'
                }`} data-testid={`streak-icon-${i}`}>
                  <Flame className={`h-3 w-3 ${entry.winning_streak >= 7 ? 'text-red-400' : entry.winning_streak >= 4 ? 'text-orange-400' : 'text-amber-400'}`} />
                  {entry.winning_streak}
                </span>
              )}
            </p>
            {type === 'games' ? (
              <p className="text-[10px] sm:text-xs text-gray-500">
                {entry.games_played}g &middot; {entry.games_won}w
                {entry.avg_time_sec != null && <span> &middot; {entry.avg_time_sec}s</span>}
              </p>
            ) : type === 'clubs' ? (
              <p className="text-[10px] sm:text-xs text-gray-500">{entry.member_count} members</p>
            ) : (
              <p className="text-[10px] sm:text-xs text-gray-500">
                {entry.movies_watched || 0}W &middot; {entry.reviews || 0}R &middot; {entry.ratings || 0}Rt
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm sm:text-lg font-bold text-amber-400">{entry.total_score || entry.total_points}</p>
            <p className="text-[9px] sm:text-xs text-gray-500">pts</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* My Clubs Points view — shows the user's clubs with their accumulated points */
const MyClubsPoints = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Users className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 opacity-30" />
        <p className="text-xs sm:text-sm">You haven't joined any clubs yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="my-clubs-points-list">
      {data.map((club, i) => (
        <div
          key={club.club_id}
          className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg transition-colors ${
            i === 0 ? 'bg-purple-900/20 border border-purple-700/40' : 'bg-zinc-800'
          }`}
          data-testid={`my-club-entry-${i}`}
        >
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-purple-700 flex items-center justify-center text-white flex-shrink-0">
            <Users className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-white font-medium truncate">{club.club_name}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">
              <span className="capitalize">{club.user_role}</span> &middot; {club.member_count} members &middot; Rank <span className="text-purple-400 font-semibold">#{club.user_rank}</span>
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm sm:text-lg font-bold text-purple-400">{club.user_points}</p>
            <p className="text-[9px] sm:text-xs text-gray-500">
              of {club.club_total_points} total
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const XchangeLeaderboard = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 opacity-30" />
        <p className="text-xs sm:text-sm">No Movie Xchange rankings yet.</p>
        <p className="text-[10px] text-gray-600 mt-1">Play games to earn your spot!</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="leaderboard-xchange-list">
      {data.map((entry, i) => (
        <div
          key={entry.user_id}
          className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg transition-colors ${
            i === 0 ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-zinc-800'
          }`}
          data-testid={`xchange-lb-entry-${i}`}
        >
          <div className="w-6 sm:w-8 flex justify-center flex-shrink-0">{getRankIcon(entry.rank)}</div>
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs sm:text-sm font-semibold flex-shrink-0 overflow-hidden">
            {entry.profile_picture
              ? <img src={entry.profile_picture} alt="" className="w-full h-full object-cover" />
              : entry.username?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-white font-medium truncate">@{entry.username}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">
              {entry.games_played}g &middot; {entry.wins}w &middot; {entry.win_rate}%
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm sm:text-lg font-bold text-emerald-400">{entry.total_score}</p>
            <p className="text-[9px] sm:text-xs text-gray-500">pts</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const Leaderboard = ({ user, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('games');
  const [period, setPeriod] = useState('daily');
  const [gamesData, setGamesData] = useState([]);
  const [moviesData, setMoviesData] = useState([]);
  const [clubsData, setClubsData] = useState([]);
  const [myClubsData, setMyClubsData] = useState([]);
  const [xchangeData, setXchangeData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      if (period === 'my_clubs') {
        if (user?.id) {
          const res = await axios.get(`${API_URL}/api/leaderboard/my-clubs?user_id=${user.id}`);
          setMyClubsData(res.data.clubs || []);
        } else {
          setMyClubsData([]);
        }
      } else if (activeTab === 'clubs') {
        const res = await axios.get(`${API_URL}/api/leaderboard/clubs`);
        setClubsData(res.data.leaderboard || []);
      } else if (activeTab === 'xchange') {
        const res = await axios.get(`${API_URL}/api/movie-stock/leaderboard`);
        setXchangeData(res.data.leaderboard || []);
      } else if (activeTab === 'games') {
        const res = await axios.get(`${API_URL}/api/leaderboard/games?period=${period}`);
        setGamesData(res.data.leaderboard || []);
      } else {
        const res = await axios.get(`${API_URL}/api/leaderboard/movies?period=${period}`);
        setMoviesData(res.data.leaderboard || []);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, period, user]);

  useEffect(() => {
    if (isOpen) fetchLeaderboard();
  }, [isOpen, fetchLeaderboard]);

  const currentData = activeTab === 'clubs' ? clubsData : activeTab === 'games' ? gamesData : activeTab === 'xchange' ? xchangeData : moviesData;
  const isMyClubs = period === 'my_clubs';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-700 text-white p-0 max-h-[85dvh] overflow-y-auto rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Trophy className="h-4 w-4 text-white" />
            </div>
            <h2 className="text-base font-bold" data-testid="leaderboard-title">Leaderboard</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="leaderboard-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Category Tabs — Games, Movies, Clubs */}
        {!isMyClubs && (
          <div className="flex border-b border-zinc-700">
            <button
              onClick={() => setActiveTab('games')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'games' ? 'border-amber-500 text-amber-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid="leaderboard-tab-games"
            >
              <Gamepad2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Games
            </button>
            <button
              onClick={() => setActiveTab('movies')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'movies' ? 'border-red-500 text-red-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid="leaderboard-tab-movies"
            >
              <Film className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Movies
            </button>
            <button
              onClick={() => setActiveTab('clubs')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'clubs' ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid="leaderboard-tab-clubs"
            >
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Clubs
            </button>
            <button
              onClick={() => setActiveTab('xchange')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'xchange' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid="leaderboard-tab-xchange"
            >
              <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Movie Xchange</span>
              <span className="sm:hidden">Xchange</span>
            </button>
          </div>
        )}

        {/* Period Tabs (hidden for Clubs and Xchange category tabs) */}
        {activeTab !== 'clubs' && activeTab !== 'xchange' && (
          <div className="px-3 pt-2.5 pb-1">
            <div className="flex gap-1 flex-wrap">
              {PERIOD_TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setPeriod(t.key)}
                  className={`px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-colors ${
                    period === t.key ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                  }`}
                  data-testid={`period-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
              {/* Club button — distinct color */}
              <button
                onClick={() => setPeriod('my_clubs')}
                className={`px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-colors flex items-center gap-1 ${
                  period === 'my_clubs'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-900/40 text-purple-300 hover:bg-purple-800/50 border border-purple-700/50'
                }`}
                data-testid="period-my-clubs"
              >
                <Shield className="h-3 w-3" />
                Club
              </button>
            </div>
            {!isMyClubs && period !== 'all_time' && (
              <p className="text-[10px] text-gray-500 mt-1.5 ml-1" data-testid="period-description">
                {getPeriodDesc(period)}
              </p>
            )}
            {isMyClubs && (
              <p className="text-[10px] text-purple-400/70 mt-1.5 ml-1" data-testid="period-description">
                Your points across all clubs
              </p>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className={`animate-spin h-7 w-7 border-2 ${isMyClubs ? 'border-purple-500' : activeTab === 'xchange' ? 'border-emerald-500' : 'border-amber-500'} border-t-transparent rounded-full`} />
            </div>
          ) : isMyClubs ? (
            <MyClubsPoints data={myClubsData} />
          ) : activeTab === 'xchange' ? (
            <XchangeLeaderboard data={xchangeData} />
          ) : (
            <LeaderboardTable data={currentData} type={activeTab} />
          )}
        </div>

        {/* Sponsor Footer */}
        <div className="border-t border-zinc-700 px-4 py-3 text-center" data-testid="leaderboard-sponsor">
          <p className="text-[10px] sm:text-xs text-gray-500">
            Leaderboard Sponsored by <span className="text-amber-400 font-semibold">Adventure Dart</span> and <span className="text-red-400 font-semibold">CasitaPlex</span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Leaderboard;
